import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Clock, User, Car, AlertTriangle, CheckCircle, Play, Pause, Maximize2,
    GripVertical, Wrench, Plus, Search, Filter, RefreshCw, MessageSquare,
    ChevronDown, X, UserPlus, MoreVertical, Package, Boxes, MapPin, Users,
    Tag, Trash2, Pencil, Hash, Activity, Calendar, Send, Tv,
} from 'lucide-react';
import Layout from './Layout';
import { TableProperties } from 'lucide-react';
import JobsPlannerPage from './JobsPlannerPage';
import JobsSearchPage from './JobsSearchPage';
import JobsToolbar from './JobsToolbar';
import { currentMonthKey } from '../utils/monthKey';
import { jobsBoardQuery } from '../utils/jobsBoardQuery';
import { fetchStaffList, fetchAllLeavePlansInRange, selectLeavePlansByStaff } from '../store/slices/staffSlice';
import {
    fetchJobs,
    fetchJob,
    fetchJobSummary,
    fetchMyJobs,
    fetchUnassignedJobs,
    startJob,
    pauseJob,
    resumeJob,
    completeJob,
    cancelJob,
    vehicleCollected,
    assignStaff,
    unassignStaff,
    openViewModal,
    closeViewModal,
    openAssignModal,
    closeAssignModal,
    openCancelConfirm,
    closeCancelConfirm,
    openCompleteConfirm,
    closeCompleteConfirm,
    openVehicleCollectedConfirm,
    closeVehicleCollectedConfirm,
    clearOperationStatus,
    setOperationSuccess,
    selectJob,
    clearSelectedJob,
    fetchJobResources,
    fetchAvailableResources,
    assignResource,
    unassignResource,
    fetchWorkCategories,
    fetchCategoryTypes,
    recordObservation,
    linkMeasurement,
    unlinkMeasurement,
    fetchAvailableMeasurements,
    updateJobStatus,
    setJobOnTv,
} from '../store/slices/jobSlice';
import { MOBILE_JOBS_PAGE_SIZE } from '../services/jobApi';
import useQueuedJobs from '../hooks/useQueuedJobs';
import PagedSearchControls from './PagedSearchControls';
import useSearchBox from '../hooks/useSearchBox';
import useDismissable from '../hooks/useDismissable';
import LaneLoading from './LaneLoading';
import { filterByNameOrRego } from '../utils/localSearch';
import { QUEUED_COLUMN_STATUSES, isQueuedStatus } from '../utils/queuedStatuses';
import { useJobDetailActions } from '../hooks/useJobDetailActions';
import JobNotesTab from './JobNotesTab';
import JobDetailModal from './JobDetailModal';
import { useOpenInvoice, useConsumeRecordParam, JOB_PARAM } from '../hooks/useRecordNavigation';
import LoanCarReturnModal from './LoanCarReturnModal';
import { getLoanCarHandoverStatusApi } from '../services/loanCarIncidentApi';
import JobsTabletView, { useIsTablet } from './JobsTabletView';
import useIsMobile from '../services/useIsMobile';
import useKeepSessionAlive from '../hooks/useKeepSessionAlive';
import MobileJobsDashboard from './MobileJobsDashboard';
import MobileShell from './mobile/MobileShell';
import { TransmissionBadge } from './TransmissionIcons';
import { suggestNextService } from '../utils/nextService';

// ── Helper functions (module-level so they never cause remounts) ──────────────

// TV Mode polling window. The board is left running unattended on a wall
// display, so it has to refresh itself — but every workshop screen tends to be
// switched on at the same time each morning, and a fixed interval would lock
// them into one synchronised burst against the backend every cycle. Re-rolling
// the delay after each tick scatters them across the window instead.
const TV_POLL_MIN_MS = 60_000;   // 1 minute
const TV_POLL_MAX_MS = 120_000;  // 2 minutes

/** Random poll delay, strictly inside (1 min, 2 min). */
const nextTvPollDelay = () =>
    Math.round(TV_POLL_MIN_MS + 1 + Math.random() * (TV_POLL_MAX_MS - TV_POLL_MIN_MS - 2));

/**
 * Compute current active seconds for a job.
 */
const getLiveActiveSeconds = (job) => {
    if (!job.actualStartDate) return 0;
    const base = job.activeSeconds ?? 0;
    if (job.status === 'IN_PROGRESS' && job.updatedAt) {
        const staleness = (Date.now() - new Date(job.updatedAt).getTime()) / 1000;
        return base + Math.max(0, staleness);
    }
    return base;
};

const getSloHours = (job) => {
    if (job.sloBudgetSeconds != null && job.sloBudgetSeconds > 0) {
        return job.sloBudgetSeconds / 3600;
    }
    return null;
};

const getSloStatus = (activeSeconds, sloBudgetSeconds) => {
    if (!sloBudgetSeconds) return 'green';
    const pct = (activeSeconds / sloBudgetSeconds) * 100;
    if (pct >= 100) return 'red';
    if (pct >= 75)  return 'amber';
    return 'green';
};

const formatTime = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
};

/**
 * Short relative time formatter for job cards — "Just now", "12m ago",
 * "2h ago", "Yesterday", "3d ago", or an absolute "17 May" once it gets
 * beyond a week. Locale-friendly via Intl.DateTimeFormat for the older
 * cases. Returns null when the input is missing or unparseable so callers
 * can omit the row entirely rather than render a stale placeholder.
 */
const formatRelativeTime = (iso) => {
    if (!iso) return null;
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return null;
    const diffMs = Date.now() - then;
    if (diffMs < 0) return 'Just now';
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return 'Just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD === 1) return 'Yesterday';
    if (diffD < 7) return `${diffD}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const getLivePausedSeconds = (job) => {
    if (!job.actualStartDate) return 0;
    let paused = job.pausedDurationSeconds ?? 0;
    if (job.status === 'PAUSED' && job.updatedAt) {
        const currentPauseSeconds = (Date.now() - new Date(job.updatedAt).getTime()) / 1000;
        paused += Math.max(0, currentPauseSeconds);
    }
    return paused;
};

const getColumnColor = (color) => {
    const colors = {
        gray: 'bg-gray-100 border-gray-300',
        blue: 'bg-blue-50 border-blue-300',
        amber: 'bg-amber-50 border-amber-300',
        purple: 'bg-purple-50 border-purple-300',
        emerald: 'bg-emerald-50 border-emerald-300',
    };
    return colors[color] || colors.gray;
};

// Status filter pills shown in the toolbar — kept identical to the tablet
// (Split) view's COLUMN_FILTERS so both views read the same. 'all' shows every
// column; a specific filter narrows the board to that single status column.
const COLUMN_FILTERS = [
    { id: 'all',         label: 'All jobs',    statuses: null },
    { id: 'queued',      label: 'Queued',       statuses: QUEUED_COLUMN_STATUSES },
    { id: 'in-progress', label: 'In progress',  statuses: ['IN_PROGRESS'] },
    { id: 'paused',      label: 'Paused',       statuses: ['PAUSED'] },
    { id: 'completed',   label: 'Completed',    statuses: ['COMPLETED'] },
];

const sloColors = {
    green:  'border-l-emerald-500 bg-white',
    amber:  'border-l-amber-500 bg-amber-50',
    red:    'border-l-red-600 bg-red-50',
    yellow: 'border-l-yellow-400 bg-yellow-50',
};

// ── Measurement threshold helpers ─────────────────────────────────────────────

/**
 * Evaluate a value string against a measurement's JSON config.
 * Mirrors the backend ThresholdEvaluator — RED wins over AMBER wins over GREEN.
 * Returns 'green' | 'amber' | 'red' | 'unclassified'.
 */
const evaluateThreshold = (value, configJson) => {
    if (!value || !configJson) return 'unclassified';
    let thresholds;
    try { thresholds = JSON.parse(configJson); } catch { return 'unclassified'; }

    const tryNumeric = (val, expr) => {
        if (!expr) return false;
        expr = expr.trim();
        try {
            const n = parseFloat(val);
            if (isNaN(n)) return false;
            if (expr.startsWith('>=')) return n >= parseFloat(expr.slice(2));
            if (expr.startsWith('>'))  return n >  parseFloat(expr.slice(1));
            if (expr.startsWith('<=')) return n <= parseFloat(expr.slice(2));
            if (expr.startsWith('<'))  return n <  parseFloat(expr.slice(1));
            if (expr.includes('-')) {
                const [lo, hi] = expr.split('-').map(s => parseFloat(s));
                return n >= lo && n <= hi;
            }
            return n === parseFloat(expr);
        } catch { return false; }
    };

    const matches = (val, expr) => {
        if (!expr) return false;
        if (tryNumeric(val, expr)) return true;
        const v = val.trim().toLowerCase();
        const e = expr.trim().toLowerCase();
        return e.includes(v) || v.includes(e);
    };

    if (matches(value, thresholds.red))   return 'red';
    if (matches(value, thresholds.amber)) return 'amber';
    if (matches(value, thresholds.green)) return 'green';
    return 'unclassified';
};

const THRESHOLD_STYLES = {
    green:        { bg: 'bg-emerald-50',  border: 'border-emerald-300', text: 'text-emerald-700', dot: 'bg-emerald-500',  inputBg: 'bg-emerald-50',  label: 'Good' },
    amber:        { bg: 'bg-amber-50',    border: 'border-amber-300',   text: 'text-amber-700',   dot: 'bg-amber-400',   inputBg: 'bg-amber-50',    label: 'Monitor' },
    red:          { bg: 'bg-red-50',      border: 'border-red-300',     text: 'text-red-700',     dot: 'bg-red-500',     inputBg: 'bg-red-50',      label: 'Action required' },
    unclassified: { bg: 'bg-white',       border: 'border-gray-200',    text: 'text-gray-400',    dot: 'bg-gray-300',    inputBg: 'bg-white',       label: '' },
};

/**
 * Slider options — order: red(0) → amber(1) → green(2), worst to best left→right.
 */
const SLIDER_OPTIONS = [
    {
        band: 'red',
        index: 0,
        icon: '✕',
        activeClass:   'bg-red-500 text-white border-red-500 scale-110 shadow-md',
        inactiveClass: 'bg-white border-red-300 text-red-500 hover:scale-105',
        hintClass:     'text-red-500',
    },
    {
        band: 'amber',
        index: 1,
        icon: '⚠',
        activeClass:   'bg-amber-400 text-white border-amber-400 scale-110 shadow-md',
        inactiveClass: 'bg-white border-amber-300 text-amber-500 hover:scale-105',
        hintClass:     'text-amber-500',
    },
    {
        band: 'green',
        index: 2,
        icon: '✓',
        activeClass:   'bg-emerald-500 text-white border-emerald-500 scale-110 shadow-md',
        inactiveClass: 'bg-white border-emerald-300 text-emerald-600 hover:scale-105',
        hintClass:     'text-emerald-600',
    },
];

const TRACK_ACTIVE_COLORS = { red: 'bg-red-400', amber: 'bg-amber-400', green: 'bg-emerald-500' };
const TRACK_FILL_PCT      = { red: '0%', amber: '50%', green: '100%' };

/**
 * Inline measurement observation row.
 */
export const ObservationRow = ({ obs, jobUuid, workItemUuid, isEditable, dispatch }) => {
    let hints = null;
    try { const c = obs.config ? JSON.parse(obs.config) : null; if (c) hints = c; } catch {}

    const bandCount = hints ? [hints.red, hints.amber, hints.green].filter(Boolean).length : 0;
    const useSlider = bandCount >= 2;

    const initialBand = (() => {
        const s = (obs.observationStatus || '').toLowerCase();
        if (s === 'green' || s === 'amber' || s === 'red') return s;
        if (obs.value) return evaluateThreshold(obs.value, obs.config);
        return null;
    })();

    const [activeBand, setActiveBand] = useState(initialBand);
    const [saving,     setSaving]     = useState(false);
    const [saveError,  setSaveError]  = useState(null);
    const [textValue,  setTextValue]  = useState(obs.value || '');
    const debounceRef                 = useRef(null);

    useEffect(() => {
        const s = (obs.observationStatus || '').toLowerCase();
        if (s === 'green' || s === 'amber' || s === 'red') {
            setActiveBand(s);
        } else if (obs.value) {
            setActiveBand(evaluateThreshold(obs.value, obs.config));
        }
        if (!useSlider) setTextValue(obs.value || '');
    }, [obs.value, obs.observationStatus]);

    const style = THRESHOLD_STYLES[activeBand || 'unclassified'];

    const save = async (value) => {
        if (!value || !value.trim()) return;
        setSaving(true);
        setSaveError(null);
        try {
            await dispatch(recordObservation({
                jobUuid,
                workItemUuid,
                measurementUuid: obs.measurementUuid,
                value: value.trim(),
            })).unwrap();
        } catch {
            setSaveError('Save failed');
        } finally {
            setSaving(false);
        }
    };

    const handleBandSelect = (option) => {
        if (!isEditable || saving) return;
        setActiveBand(option.band);
        setSaveError(null);
        const bandValue = hints?.[option.band] ?? option.band;
        save(bandValue);
    };

    const handleTextChange = (e) => {
        const val = e.target.value;
        setTextValue(val);
        setSaveError(null);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => save(val), 800);
    };

    const activeIndex = SLIDER_OPTIONS.findIndex(o => o.band === activeBand);

    return (
        <div className={`rounded-lg border p-3 transition-all duration-200 ${style.bg} ${style.border}`}>
            <div className="flex items-center gap-2 mb-3">
                <span className={`w-2 h-2 rounded-full shrink-0 transition-colors duration-200 ${style.dot}`} />
                <span className="text-xs font-semibold text-gray-700 flex-1 leading-tight">{obs.name}</span>
                {obs.unitSymbol && (
                    <span className="text-xs text-gray-400 font-mono shrink-0">{obs.unitSymbol}</span>
                )}
                {activeBand && activeBand !== 'unclassified' && (
                    <span className={`text-xs font-bold shrink-0 ${style.text}`}>{style.label}</span>
                )}
                {saving && <RefreshCw size={11} className="animate-spin text-gray-400 shrink-0" />}
            </div>

            {isEditable && useSlider && (
                <div>
                    <div className="relative flex items-center">
                        <div className="absolute inset-x-5 h-1 rounded-full bg-gray-200 top-5 z-0" />
                        {activeIndex >= 0 && (
                            <div
                                className={`absolute left-5 h-1 rounded-full top-5 z-0 transition-all duration-300 ${TRACK_ACTIVE_COLORS[activeBand]}`}
                                style={{ width: TRACK_FILL_PCT[activeBand] }}
                            />
                        )}
                        {SLIDER_OPTIONS.map((option) => {
                            const isActive = option.band === activeBand;
                            return (
                                <button
                                    key={option.band}
                                    onClick={() => handleBandSelect(option)}
                                    disabled={saving}
                                    title={hints?.[option.band] ?? option.band}
                                    className="relative z-10 flex-1 flex flex-col items-center gap-1 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <span className={`w-10 h-10 rounded-full border-2 flex items-center justify-center text-base font-bold transition-all duration-200 ${isActive ? option.activeClass : option.inactiveClass}`}>
                                        {option.icon}
                                    </span>
                                    <span className={`text-xs font-medium text-center leading-tight max-w-[72px] truncate transition-colors ${isActive ? option.hintClass : 'text-gray-400'}`}>
                                        {hints?.[option.band] ?? option.band}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    {saveError && <p className="text-xs text-red-500 mt-2 text-center">{saveError}</p>}
                </div>
            )}

            {isEditable && !useSlider && (
                <div>
                    <input
                        type="text"
                        value={textValue}
                        onChange={handleTextChange}
                        placeholder="Enter value…"
                        className={`w-full border rounded-lg px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-400 transition-colors ${style.inputBg} ${style.border}`}
                    />
                    {saveError && <p className="text-xs text-red-500 mt-1">{saveError}</p>}
                </div>
            )}

            {!isEditable && (
                <div className={`px-3 py-1.5 rounded-lg text-sm font-mono ${style.inputBg} border ${style.border}`}>
                    {(useSlider ? (hints?.[activeBand] ?? activeBand) : textValue)
                        || <span className="text-gray-400 italic">No value recorded</span>}
                </div>
            )}
        </div>
    );
};

// ── AddMeasurementPicker ──────────────────────────────────────────────────────

/**
 * Modal picker for linking a measurement from the org's catalogue
 * (org-owned + global) to an ad-hoc work item. After link succeeds the
 * ObservationRow renders immediately (placeholder value, UNCLASSIFIED status)
 * and the technician can record a value via the existing recordObservation flow.
 *
 * Only renders for ad-hoc items — caller is responsible for gating on
 * `!workItem.fromServiceProfile`.
 *
 * Props:
 *   jobUuid       — UUID of the parent job
 *   workItem      — the JobWorkItem (uses uuid + measurementObservations to filter)
 *   onClose       — close-modal callback
 */
export const AddMeasurementPicker = ({ jobUuid, workItem, onClose }) => {
    const dispatch = useDispatch();
    const {
        availableMeasurements,
        availableMeasurementsLoading,
        availableMeasurementsLoaded,
    } = useSelector((s) => s.jobs);

    const [search,     setSearch]     = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [linkError,  setLinkError]  = useState(null);

    // Lazy-load the catalogue the first time the picker is opened in the session
    useEffect(() => {
        if (!availableMeasurementsLoaded && !availableMeasurementsLoading) {
            dispatch(fetchAvailableMeasurements({}));
        }
    }, [dispatch, availableMeasurementsLoaded, availableMeasurementsLoading]);

    // Hide measurements already linked to this work item to prevent dupes
    const linkedUuids = new Set(
        (workItem?.measurementObservations || []).map((o) => o.measurementUuid)
    );

    const q = search.trim().toLowerCase();
    const filtered = (availableMeasurements || [])
        .filter((m) => !linkedUuids.has(m.uuid))
        .filter((m) => !q
            || (m.name && m.name.toLowerCase().includes(q))
            || (m.measurementType && String(m.measurementType).toLowerCase().includes(q)));

    const handlePick = async (measurementUuid) => {
        setSubmitting(true);
        setLinkError(null);
        try {
            await dispatch(linkMeasurement({
                jobUuid,
                workItemUuid: workItem.uuid,
                measurementUuid,
            })).unwrap();
            onClose();
        } catch (err) {
            setLinkError(typeof err === 'string' ? err : 'Failed to add measurement');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl w-full max-w-md flex flex-col max-h-[80vh] shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-5 py-4 border-b flex items-center justify-between shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">Add measurement</h2>
                        <p className="text-sm text-gray-500 mt-0.5 truncate max-w-xs">
                            {workItem?.description || 'Work item'}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-xl text-gray-500"
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="p-4 border-b shrink-0">
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search measurements…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            autoFocus
                            className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                        />
                    </div>
                    {linkError && (
                        <p className="text-xs text-red-600 mt-2">{linkError}</p>
                    )}
                </div>

                <div className="overflow-y-auto flex-1 px-2 py-2">
                    {availableMeasurementsLoading && (
                        <div className="py-8 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
                            <RefreshCw size={14} className="animate-spin" />
                            Loading measurements…
                        </div>
                    )}

                    {!availableMeasurementsLoading && filtered.length === 0 && (
                        <div className="py-8 text-center text-sm text-gray-500">
                            {availableMeasurements.length === 0
                                ? 'No measurements have been defined yet. Add some in Settings → Measurements.'
                                : linkedUuids.size > 0 && filtered.length === 0 && !q
                                    ? 'All available measurements are already linked.'
                                    : 'No measurements match your search.'}
                        </div>
                    )}

                    {filtered.map((m) => {
                        const isGlobal = m.organisationUuid == null && m.organisation == null;
                        const typeLabel = m.measurementTypeDisplay
                            || (m.measurementType ? String(m.measurementType) : null);
                        return (
                            <button
                                key={m.uuid}
                                onClick={() => handlePick(m.uuid)}
                                disabled={submitting}
                                className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-violet-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-start gap-3 group"
                            >
                                <Activity
                                    size={16}
                                    className="text-violet-500 mt-0.5 shrink-0 group-hover:text-violet-700"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-900 truncate">{m.name}</div>
                                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                                        {typeLabel && <span>{typeLabel}</span>}
                                        {isGlobal && (
                                            <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-medium uppercase">
                                                Global
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <Plus
                                    size={14}
                                    className="text-gray-300 group-hover:text-violet-600 mt-1 shrink-0"
                                />
                            </button>
                        );
                    })}
                </div>

                <div className="px-5 py-3 border-t bg-gray-50 rounded-b-2xl shrink-0 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Inject keyframes ──────────────────────────────────────────────────────────

if (typeof document !== 'undefined' && !document.getElementById('wadakaroo-heartbeat')) {
    const style = document.createElement('style');
    style.id = 'wadakaroo-heartbeat';
    style.textContent = `
        @keyframes heartbeat {
            0%   { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.55); }
            40%  { box-shadow: 0 0 0 8px rgba(220, 38, 38, 0); }
            100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
        }
        .job-card-overdue {
            animation: heartbeat 1.6s ease-in-out infinite;
            border-color: rgb(220 38 38) !important;
        }
        @keyframes toast-slide-in {
            from { transform: translateX(calc(100% + 24px)); opacity: 0; }
            to   { transform: translateX(0);                 opacity: 1; }
        }
        @keyframes toast-slide-out {
            from { transform: translateX(0);                 opacity: 1; }
            to   { transform: translateX(calc(100% + 24px)); opacity: 0; }
        }
        @keyframes toast-progress {
            from { width: 100%; }
            to   { width: 0%;   }
        }
        @keyframes toast-shake {
            0%,100% { transform: translateX(0); }
            15%     { transform: translateX(-6px); }
            30%     { transform: translateX(6px); }
            45%     { transform: translateX(-5px); }
            60%     { transform: translateX(5px); }
            75%     { transform: translateX(-3px); }
            90%     { transform: translateX(3px); }
        }
        .toast-enter {
            animation: toast-slide-in 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        .toast-enter-shake {
            animation: toast-slide-in 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards,
                       toast-shake 0.55s ease-in-out 0.35s;
        }
        .toast-exit {
            animation: toast-slide-out 0.3s ease-in forwards;
        }
        .toast-progress-bar {
            animation: toast-progress linear forwards;
        }
    `;
    document.head.appendChild(style);
}

// ── JobCard ───────────────────────────────────────────────────────────────────

const JobCard = ({ job, currentTime, onView, onStart, onPause, onResume, onComplete, onCancel, onNote, onAssign, onToggleTv, onDragStart, onDragEnd, isDragging }) => {
    const activeSeconds = getLiveActiveSeconds(job);
    const sloHours      = getSloHours(job);
    const sloBudgetSec  = job.sloBudgetSeconds ?? null;
    const sloStatus     = (job.status === 'IN_PROGRESS' || job.status === 'PAUSED')
        ? getSloStatus(activeSeconds, sloBudgetSec)
        : 'green';
    const isOverdue     = job.overdue === true || sloStatus === 'red';
    // The card-tint stays narrow — yellow only when a paused job is waiting on
    // the customer (the original intent: visually disambiguate the paused
    // column). The badge below uses the broader signal so any job currently
    // waiting on customer approval surfaces a Requested pill, regardless of
    // its lifecycle status.
    const isPausedAwaiting       = job.status === 'PAUSED' && job.hasAwaitingConfirmation === true;
    const isAwaitingConfirmation = job.hasAwaitingConfirmation === true;
    const cardColorClass = isPausedAwaiting ? sloColors.yellow : sloColors[sloStatus];
    const [showActions, setShowActions] = useState(false);
    // Escape, or a click anywhere else. The menu used to close only on a second click
    // of its own button, so one opened by accident sat over the board for ever.
    const actionsRef = useDismissable(showActions, () => setShowActions(false));

    return (
        <div
            draggable={!!onDragStart}
            onDragStart={(e) => {
                // Set the payload before invoking the parent's handler so the
                // browser starts the drag with valid data. effectAllowed=move
                // gives the cursor the right visual cue (no "copy" plus icon).
                if (onDragStart) {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/job-uuid', job.uuid);
                    onDragStart(job);
                }
            }}
            onDragEnd={() => { if (onDragEnd) onDragEnd(); }}
            className={`p-3 rounded-xl border-l-4 shadow-sm mb-3 cursor-pointer transition-all hover:shadow-md ${cardColorClass} ${isOverdue ? 'job-card-overdue ring-2 ring-red-500' : ''} ${isDragging ? 'opacity-40' : ''}`}
            onClick={() => onView(job)}
        >
            <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                    <GripVertical size={14} className="text-gray-300" />
                    <span className="font-semibold text-gray-900 text-sm truncate max-w-[120px]">
                        {job.customerName}
                    </span>
                    {/* Picked for the wall. Small, because it is a marker, not a status. */}
                    {job.onTv && (
                        <Tv size={12} className="text-violet-600 shrink-0" data-testid="on-tv-icon" aria-label="On TV" />
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {job.priority === 'URGENT' && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium flex items-center gap-1">
                            <AlertTriangle size={10} />
                            URGENT
                        </span>
                    )}
                    {job.priority === 'HIGH' && (
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                            HIGH
                        </span>
                    )}
                    <div className="relative" ref={actionsRef}>
                        <button
                            onClick={(e) => { e.stopPropagation(); setShowActions(!showActions); }}
                            aria-haspopup="menu"
                            aria-expanded={showActions}
                            aria-label="Job actions"
                            className="p-1 hover:bg-gray-100 rounded"
                        >
                            <MoreVertical size={14} className="text-gray-400" />
                        </button>
                        {showActions && (
                            <div className="absolute right-0 top-6 z-10 bg-white border rounded-lg shadow-lg py-1 min-w-[120px]">
                                {job.status === 'IN_PROGRESS' && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onPause(job); setShowActions(false); }}
                                        className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                                    >
                                        <Pause size={14} /> Pause
                                    </button>
                                )}
                                {job.status === 'PAUSED' && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onResume(job); setShowActions(false); }}
                                        className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                                    >
                                        <Play size={14} /> Resume
                                    </button>
                                )}
                                {isQueuedStatus(job.status) && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onStart(job); setShowActions(false); }}
                                        className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                                    >
                                        <Play size={14} /> Start
                                    </button>
                                )}
                                {job.status === 'IN_PROGRESS' && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onComplete(job); setShowActions(false); }}
                                        className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 flex items-center gap-2 text-emerald-600"
                                    >
                                        <CheckCircle size={14} /> Complete
                                    </button>
                                )}
                                <button
                                    onClick={(e) => { e.stopPropagation(); onNote(job); setShowActions(false); }}
                                    className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                                >
                                    <MessageSquare size={14} /> Add Note
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); onAssign(job); setShowActions(false); }}
                                    className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                                >
                                    <UserPlus size={14} /> Assign
                                </button>
                                {/* TV Mode shows only the jobs somebody picked for the
                                    wall. This is the pick, on every column. */}
                                {onToggleTv && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onToggleTv(job); setShowActions(false); }}
                                        className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                                    >
                                        <Tv size={14} /> {job.onTv ? 'Remove from TV' : 'Show on TV'}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-2 flex-wrap">
                <Car size={12} className="shrink-0" />
                {/* Vehicle description may arrive with a leading "0 " when the
                    backend has no year for the car (year=0). Strip it so the
                    card reads "Honda Civic" rather than "0 Honda Civic". */}
                <span className="truncate flex-1 min-w-0">{(job.vehicleDescription || '').replace(/^0\s+/, '') || job.vehicleRego}</span>
                <TransmissionBadge value={job.vehicleTransmission} variant="pill" />
            </div>

            <div className="bg-gray-100 rounded-lg px-2 py-1 mb-2">
                <span className="text-xs font-medium text-gray-700 truncate block">
                    {job.jobNumber}
                </span>
            </div>

            {/* Customer-approval signal pills.
                All hidden on COMPLETED jobs — once the work is finished these
                are no longer actionable, just noise on a completed-column tile.

                Per-pill visibility on non-completed jobs:
                  • "Approval Requested for Unapproved" (yellow) — visible in
                    every non-completed status. A paused or in-progress job
                    can still be waiting on a customer response (partial work
                    underway plus an in-flight request), so the tech needs
                    this signal at any stage.
                  • "Can't start work without approval" (red, NON_APPROVED) —
                    QUEUED ONLY. The whole message is "before you start"; once
                    the job is IN_PROGRESS or PAUSED, the tech has already
                    started and the warning is either too late or contradicts
                    reality. Suppressing it past queued keeps the card honest.
                  • "Can work on approved items" (green, PARTIALLY_APPROVED or
                    ALL_APPROVED) — QUEUED ONLY. The label reads as a "you
                    *can* start" prompt; once work is underway it's redundant.
                  • "All Declined" (red) — visible in every non-completed
                    status. On a queued job it blocks the start; on an in-
                    progress/paused job it means the customer pulled the rug
                    on work already underway, which is more urgent, not less.

                Queued = CREATED / QUOTED / APPROVED (matches the kanban
                column grouping defined further down in this file). */}
            {job.status !== 'COMPLETED' && (() => {
                const isQueued = job.status === 'CREATED'
                    || job.status === 'QUOTED'
                    || job.status === 'APPROVED';
                return (
                    <>
                        {isAwaitingConfirmation && (
                            <div className="flex items-center gap-1.5 mb-2 px-2 py-1 bg-yellow-100 border border-yellow-300 rounded-lg">
                                <Send size={11} className="text-yellow-600 shrink-0" />
                                <span className="text-xs font-semibold text-yellow-700">Approval Requested for Unapproved</span>
                            </div>
                        )}

                        {isQueued && job.customerBasedWorkItemStatus === 'NON_APPROVED' && (
                            <div className="flex items-center gap-1.5 mb-2 px-2 py-1 bg-red-100 border border-red-300 rounded-lg">
                                <AlertTriangle size={11} className="text-red-600 shrink-0" />
                                <span className="text-xs font-semibold text-red-700">Can't start work without approval</span>
                            </div>
                        )}

                        {isQueued && (job.customerBasedWorkItemStatus === 'PARTIALLY_APPROVED'
                            || job.customerBasedWorkItemStatus === 'ALL_APPROVED') && (
                            <div className="flex items-center gap-1.5 mb-2 px-2 py-1 bg-emerald-100 border border-emerald-300 rounded-lg">
                                <CheckCircle size={11} className="text-emerald-600 shrink-0" />
                                <span className="text-xs font-semibold text-emerald-700">Can work on approved items</span>
                            </div>
                        )}

                        {job.customerBasedWorkItemStatus === 'ALL_DECLINED' && (
                            <div className="flex items-center gap-1.5 mb-2 px-2 py-1 bg-red-100 border border-red-300 rounded-lg">
                                <AlertTriangle size={11} className="text-red-600 shrink-0" />
                                <span className="text-xs font-semibold text-red-700">All Declined</span>
                            </div>
                        )}
                    </>
                );
            })()}

            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                        <User size={12} />
                        <span>{job.assignedStaffName || 'Unassigned'}</span>
                    </div>
                </div>
                {job.workItemCount > 0 && (
                    <span className="text-xs font-medium text-gray-600 flex items-center gap-1">
                        <Wrench size={14} className="text-gray-500" />
                        {job.workItemCount} {job.workItemCount === 1 ? 'item' : 'items'}
                    </span>
                )}
            </div>

            {/* Created-at — relative time so techs can see at a glance how stale
                the card is without doing date math. Falls back gracefully if
                the backend ever returns the field missing. */}
            {formatRelativeTime(job.createdAt) && (
                <div className="flex items-center gap-1 text-[11px] text-gray-400 mb-2">
                    <Calendar size={11} />
                    <span>Created {formatRelativeTime(job.createdAt)}</span>
                </div>
            )}

            {(activeSeconds > 0 || (job.pausedDurationSeconds ?? 0) > 0) && (() => {
                const pausedSec   = job.status === 'PAUSED'
                    ? getLivePausedSeconds(job)
                    : (job.pausedDurationSeconds ?? 0);
                const workedHours = activeSeconds / 3600;
                const pausedHours = pausedSec / 3600;
                const budgetHours = sloHours;
                const showWaitTime = pausedSec > 0;
                return (
                    <>
                        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                            {activeSeconds > 0 && (
                                <span className="flex items-center gap-1 text-xs font-mono font-semibold text-emerald-600">
                                    <Clock size={11} />
                                    {formatTime(workedHours)}
                                </span>
                            )}
                            {showWaitTime && (
                                <span className="flex items-center gap-1 text-xs font-mono font-semibold text-amber-500">
                                    <Pause size={11} />
                                    {formatTime(pausedHours)}
                                </span>
                            )}
                            {budgetHours && (
                                <span className={`text-xs font-mono ${isOverdue ? 'text-red-500 font-bold' : 'text-gray-400'}`}>
                                    / {formatTime(budgetHours)}
                                </span>
                            )}
                        </div>
                    </>
                );
            })()}

            {job.totalEstimatedCost > 0 && (
                <div className="mt-2 text-right">
                    <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mr-1">Est</span>
                    <span className="text-sm font-bold text-gray-900">
                        ${job.totalEstimatedCost.toFixed(2)}
                    </span>
                </div>
            )}
        </div>
    );
};

// ── AddWorkItemModal ──────────────────────────────────────────────────────────

const WORK_ITEM_STATUS_TRANSITIONS = {
    PENDING:               ['IN_PROGRESS', 'AWAITING_CONFIRMATION', 'DECLINED'],
    AWAITING_CONFIRMATION: ['CONFIRMED', 'DECLINED'],
    CONFIRMED:             ['IN_PROGRESS'],
    IN_PROGRESS:           ['COMPLETED', 'AWAITING_CONFIRMATION'],
    COMPLETED:             [],
    DECLINED:              [],
};

const WORK_ITEM_STATUS_META = {
    PENDING:               { label: 'Pending',               color: 'bg-gray-100 text-gray-600' },
    AWAITING_CONFIRMATION: { label: 'Awaiting Confirmation',  color: 'bg-amber-100 text-amber-700' },
    CONFIRMED:             { label: 'Confirmed',              color: 'bg-sky-100 text-sky-700' },
    IN_PROGRESS:           { label: 'In Progress',            color: 'bg-blue-100 text-blue-700' },
    COMPLETED:             { label: 'Completed',              color: 'bg-emerald-100 text-emerald-700' },
    DECLINED:              { label: 'Declined',               color: 'bg-red-100 text-red-700' },
};

const EMPTY_FORM = {
    description: '',
    detailNotes: '',
    workCategory: '',
    confirmationRequired: false,
    confirmationMessage: '',
    status: '',
    cdLabourValue: '',
    cdLabourDescription: '',
    cdParts: [],
};

const EMPTY_PART = { partNo: '', value: '', description: '' };

const buildCostDetail = (form) => {
    const hasLabour = form.cdLabourValue !== '' || form.cdLabourDescription !== '';
    const hasParts  = form.cdParts.some(p => p.partNo !== '' || p.value !== '' || p.description !== '');
    if (!hasLabour && !hasParts) return null;

    const detail = {};
    if (hasLabour) {
        detail.labour = {
            value:       form.cdLabourValue !== '' ? Number(form.cdLabourValue) : 0,
            description: form.cdLabourDescription.trim(),
        };
    }
    if (hasParts) {
        detail.parts = form.cdParts
            .filter(p => p.partNo !== '' || p.value !== '' || p.description !== '')
            .map(p => ({
                partNo:      p.partNo.trim(),
                value:       p.value !== '' ? Number(p.value) : 0,
                description: p.description.trim(),
            }));
    }
    return detail;
};

const flattenCostDetail = (costDetail) => {
    if (!costDetail) return { cdLabourValue: '', cdLabourDescription: '', cdParts: [] };
    return {
        cdLabourValue:       costDetail.labour?.value       != null ? String(costDetail.labour.value) : '',
        cdLabourDescription: costDetail.labour?.description ?? '',
        cdParts:             (costDetail.parts ?? []).map(p => ({
            partNo:      p.partNo      ?? '',
            value:       p.value       != null ? String(p.value) : '',
            description: p.description ?? '',
        })),
    };
};

const AddWorkItemModal = ({ job, onClose, onSubmit, loading, error, initialValues = null, mode = 'add', workCategories = [] }) => {
    const baseForm = initialValues
        ? {
            ...EMPTY_FORM,
            ...initialValues,
            ...flattenCostDetail(initialValues.costDetail),
        }
        : EMPTY_FORM;

    const [form, setForm] = useState(baseForm);
    const [errors, setErrors] = useState({});

    const currentStatus = initialValues?.status || null;
    const isTerminal = currentStatus && WORK_ITEM_STATUS_TRANSITIONS[currentStatus]?.length === 0;
    const nextOptions = currentStatus ? WORK_ITEM_STATUS_TRANSITIONS[currentStatus] ?? [] : [];

    const set = (field, value) => {
        setForm(f => ({ ...f, [field]: value }));
        if (errors[field]) setErrors(e => ({ ...e, [field]: null }));
    };

    const validate = () => {
        const e = {};
        if (!form.description.trim()) e.description = 'Description is required';
        else if (form.description.length > 500) e.description = 'Max 500 characters';
        if (form.detailNotes?.length > 2000) e.detailNotes = 'Max 2000 characters';
        if (form.confirmationRequired && !form.confirmationMessage?.trim()) e.confirmationMessage = 'Message is required when confirmation is enabled';
        if (form.confirmationMessage?.length > 1000) e.confirmationMessage = 'Max 1000 characters';

        const labourFilled = form.cdLabourValue !== '' || form.cdLabourDescription !== '';
        if (labourFilled) {
            if (form.cdLabourValue === '') e.cdLabourValue = 'Labour value is required';
            else if (isNaN(Number(form.cdLabourValue)) || Number(form.cdLabourValue) < 0) e.cdLabourValue = 'Must be a number ≥ 0';
            if (!form.cdLabourDescription.trim()) e.cdLabourDescription = 'Labour description is required';
            else if (form.cdLabourDescription.length > 500) e.cdLabourDescription = 'Max 500 characters';
        }
        form.cdParts.forEach((p, i) => {
            const partFilled = p.partNo !== '' || p.value !== '' || p.description !== '';
            if (partFilled) {
                if (!p.partNo.trim()) e[`cdPart_${i}_partNo`] = 'Part number is required';
                else if (p.partNo.length > 100) e[`cdPart_${i}_partNo`] = 'Max 100 characters';
                if (p.value === '') e[`cdPart_${i}_value`] = 'Value is required';
                else if (isNaN(Number(p.value)) || Number(p.value) < 0) e[`cdPart_${i}_value`] = 'Must be a number ≥ 0';
                if (!p.description.trim()) e[`cdPart_${i}_description`] = 'Description is required';
                else if (p.description.length > 500) e[`cdPart_${i}_description`] = 'Max 500 characters';
            }
        });

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;
        const payload = {
            description:          form.description.trim(),
            detailNotes:          form.detailNotes?.trim() || null,
            workCategory:         form.workCategory || null,
            confirmationRequired: form.confirmationRequired || false,
            confirmationMessage:  form.confirmationRequired ? (form.confirmationMessage?.trim() || null) : null,
            costDetail:           buildCostDetail(form),
        };
        if (mode === 'edit' && form.status && form.status !== currentStatus) {
            payload.status = form.status;
        }
        onSubmit(payload);
    };

    const addPart    = () => setForm(f => ({ ...f, cdParts: [...f.cdParts, { ...EMPTY_PART }] }));
    const removePart = (i) => setForm(f => ({ ...f, cdParts: f.cdParts.filter((_, idx) => idx !== i) }));
    const setPart    = (i, field, value) => {
        setForm(f => {
            const parts = f.cdParts.map((p, idx) => idx === i ? { ...p, [field]: value } : p);
            return { ...f, cdParts: parts };
        });
        if (errors[`cdPart_${i}_${field}`]) setErrors(e => ({ ...e, [`cdPart_${i}_${field}`]: null }));
    };

    const inputCls = (field) =>
        `w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${
            errors[field] ? 'border-red-400 bg-red-50' : 'border-gray-200'
        }`;

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg flex flex-col max-h-[92vh] shadow-2xl">

                <div className="px-6 py-4 border-b flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-violet-100 rounded-xl flex items-center justify-center">
                            <Tag size={16} className="text-violet-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">{mode === 'edit' ? 'Edit Work Item' : 'Add Work Item'}</h2>
                            <p className="text-xs text-gray-400 mt-0.5">{job.jobNumber} · {job.customerName}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl">
                        <X size={18} />
                    </button>
                </div>

                <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">

                    {error && (
                        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5 rounded-xl">
                            <AlertTriangle size={14} className="shrink-0" />
                            {error}
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                            Description <span className="text-red-400">*</span>
                        </label>
                        <input
                            type="text"
                            value={form.description}
                            onChange={e => set('description', e.target.value)}
                            placeholder="e.g. Front Brake Pad Replacement"
                            className={inputCls('description')}
                            autoFocus
                        />
                        {errors.description && <p className="text-xs text-red-500 mt-1">{errors.description}</p>}
                    </div>

                    <div className={mode === 'edit' && currentStatus ? 'grid grid-cols-2 gap-3' : ''}>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                                Work Category
                            </label>
                            <select
                                value={form.workCategory}
                                onChange={e => set('workCategory', e.target.value)}
                                className={inputCls('workCategory') + ' bg-white'}
                            >
                                <option value="">— None —</option>
                                {workCategories
                                    .filter(t => t.label !== 'Not Supported')
                                    .map(t => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                            </select>
                        </div>

                        {mode === 'edit' && currentStatus && (
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                                    Status
                                </label>
                                {isTerminal ? (
                                    <div className="flex items-center h-[38px]">
                                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${WORK_ITEM_STATUS_META[currentStatus]?.color}`}>
                                            {WORK_ITEM_STATUS_META[currentStatus]?.label ?? currentStatus}
                                        </span>
                                    </div>
                                ) : (
                                    <select
                                        value={form.status}
                                        onChange={e => set('status', e.target.value)}
                                        className={inputCls('status') + ' bg-white'}
                                    >
                                        <option value="">
                                            {WORK_ITEM_STATUS_META[currentStatus]?.label ?? currentStatus} (current)
                                        </option>
                                        {nextOptions.map(s => (
                                            <option key={s} value={s}>
                                                → {WORK_ITEM_STATUS_META[s]?.label ?? s}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Labour & Parts section omitted for brevity — identical to original */}

                </div>

                <div className="px-6 py-4 border-t flex gap-2 shrink-0">
                    <button onClick={onClose} className="flex-1 py-2 border rounded-xl hover:bg-gray-50 text-sm">
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="flex-1 py-2 bg-violet-600 text-white rounded-xl hover:bg-violet-700 disabled:opacity-50 text-sm font-medium flex items-center justify-center gap-2"
                    >
                        {loading ? (
                            <><RefreshCw size={14} className="animate-spin" /> Saving…</>
                        ) : mode === 'edit' ? (
                            <><Pencil size={14} /> Save Changes</>
                        ) : (
                            <><Plus size={14} /> Add Work Item</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Toast ─────────────────────────────────────────────────────────────────────

const TOAST_DURATION = 5000;

const Toast = ({ message, type, onDismiss }) => {
    const [exiting, setExiting] = useState(false);

    const dismiss = useCallback(() => {
        setExiting(true);
        setTimeout(onDismiss, 300);
    }, [onDismiss]);

    useEffect(() => {
        const t = setTimeout(dismiss, TOAST_DURATION);
        return () => clearTimeout(t);
    }, [dismiss]);

    const isSuccess = type === 'success';

    return (
        <div
            className={`${exiting ? 'toast-exit' : isSuccess ? 'toast-enter' : 'toast-enter-shake'} fixed top-4 right-4 z-[100] flex flex-col overflow-hidden rounded-xl shadow-xl min-w-[300px] max-w-sm`}
        >
            <div className={`flex items-start gap-3 px-4 py-3.5 ${isSuccess ? 'bg-emerald-100 border border-emerald-200' : 'bg-red-100 border border-red-200'}`}>
                <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5 ${isSuccess ? 'bg-emerald-200' : 'bg-red-200'}`}>
                    {isSuccess
                        ? <CheckCircle size={15} className="text-emerald-700" />
                        : <AlertTriangle size={15} className="text-red-700" />
                    }
                </div>
                <p className="flex-1 text-sm text-black leading-snug pt-0.5 font-medium">{message}</p>
                <button
                    onClick={dismiss}
                    className={`shrink-0 mt-0.5 p-1 rounded-lg transition-colors ${isSuccess ? 'text-emerald-600 hover:bg-emerald-200' : 'text-red-600 hover:bg-red-200'}`}
                    aria-label="Dismiss"
                >
                    <X size={14} />
                </button>
            </div>
            <div className={`h-1 w-full ${isSuccess ? 'bg-emerald-200' : 'bg-red-200'}`}>
                <div
                    className={`h-full toast-progress-bar ${isSuccess ? 'bg-emerald-500' : 'bg-red-500'}`}
                    style={{ animationDuration: `${TOAST_DURATION}ms` }}
                />
            </div>
        </div>
    );
};

// ── JobsPage ──────────────────────────────────────────────────────────────────

const JobsPage = () => {
    const dispatch = useDispatch();
    // Following a job's invoice link, without a page reload.
    const openInvoice = useOpenInvoice();

    const {
        jobs,
        pagination,
        selectedJob,
        summary,
        jobsLoading,
        operationLoading,
        // The open card's own fetch — never operationLoading, which is every write.
        detailLoading,
        operationError,
        operationSuccess,
        showViewModal,
        showAssignModal,
        showCancelConfirm,
        showCompleteConfirm,
        showVehicleCollectedConfirm,
        jobResources,
        availableResources,
        resourcesLoading,
        workCategories,
        categoryTypes,
    } = useSelector(state => state.jobs);

    const { staffList, isLoading: staffLoading } = useSelector(state => state.staff);
    const leavePlansByStaff                      = useSelector(selectLeavePlansByStaff);

    const isTablet                               = useIsTablet();
    const isMobile                               = useIsMobile();

    /*
     * Phone scope: the whole workshop by default, with a Me toggle to narrow.
     *
     * This used to be hardcoded `myJobsOnly: isMobile`, so on a phone EVERY user saw
     * only jobs assigned to them — including the owner, who could not see their own
     * workshop's board from a phone at all. The default is now the org view, matching
     * desktop, and "Me" is the deliberate narrowing rather than the other way round.
     */
    const [mobileMineOnly, setMobileMineOnly] = useState(false);
    const [forceTabletView, setForceTabletView] = useState(false);
    const showTabletView                        = !isMobile && (isTablet || forceTabletView);

    const [isFullscreen, setIsFullscreen]       = useState(false);
    const [showPlanner, setShowPlanner]         = useState(false);
    const [showSearch, setShowSearch]           = useState(false);
    const [initialModalTab, setInitialModalTab] = useState('details');
    const [currentTime, setCurrentTime]         = useState(new Date());
    const [columnFilter, setColumnFilter]       = useState('all');

    // ── The Queued column's own page and LOCAL search ──
    // The header search box is gone. The one column that needs searching is the one
    // that grows without bound, and it comes a hundred at a time from its own endpoint;
    // the box narrows THAT page in the browser by name or rego, and the server is
    // never asked. See useQueuedJobs and filterByNameOrRego.
    // Two speeds: `text` narrows the loaded page on the keystroke, `q` asks the server
    // once the typing settles — the page holds twenty of possibly thousands, so the
    // answer is usually not on it. See useSearchBox.
    const queuedBox = useSearchBox();
    const queuedJobs = useQueuedJobs({
        enabled: !isMobile, onTv: isFullscreen, q: queuedBox.q,
    });

    // ── Which lanes are loading ──
    // Each column waits on the SOURCE that feeds it: Queued on the queued page (so
    // paging shows the wait there and nowhere else), the other three on the month
    // query. In TV Mode the poll refreshes every minute or two; blanking the wall for
    // each one would flash it, so once a source has loaded the wall keeps showing what
    // it has and only the FIRST load waits.
    const queuedLoadedOnce = useRef(false);
    const monthLoadedOnce  = useRef(false);
    if (!queuedJobs.loading) queuedLoadedOnce.current = true;
    if (!jobsLoading) monthLoadedOnce.current = true;
    const queuedWaiting = queuedJobs.loading && !(isFullscreen && queuedLoadedOnce.current);
    const monthWaiting  = jobsLoading && !(isFullscreen && monthLoadedOnce.current);

    // The phone's all-jobs list pages the month query a hundred at a time; "mine"
    // stays one list. A change of scope starts again from page one.
    const [mobilePage, setMobilePage] = useState(0);
    /**
     * The phone's search, once its typing has settled.
     *
     * <p>The box is rendered by MobileJobsDashboard, which narrows the loaded page on
     * every keystroke; this is the term it hands back for the SERVER, where the pages
     * it cannot see are. A new term starts again from page one.
     */
    const [mobileSearch, setMobileSearch] = useState(null);
    const onMobileSearch = useCallback((q) => {
        setMobileSearch(q);
        setMobilePage(0);
    }, []);
    useEffect(() => { setMobilePage(0); }, [mobileMineOnly]);

    const [cancelReason, setCancelReason]       = useState('');

    // What the workshop is about to tell the customer. Prefilled when the collect
    // dialog opens — see the effect below — because this is a confirmation, not a
    // calculation: a blank box gets skipped and then nobody is told anything.
    const [nextServiceDue, setNextServiceDue] = useState('');
    const [nextServiceKm, setNextServiceKm]   = useState('');
    const [odometerOut, setOdometerOut]         = useState('');
    const [staffSearch, setStaffSearch]         = useState('');
    const [resourceTypeFilter, setResourceTypeFilter] = useState('');

    // ── Kanban drag-and-drop state ──
    // Dragging a JobCard between columns dispatches updateJobStatus on drop.
    // Tracking the dragged job's uuid lets us dim the source card while in
    // flight; tracking the hovered column id lets us highlight the drop
    // target so the gesture lands clearly. Both are cleared on drop OR on
    // dragend, so an aborted drag (Escape, off-screen drop) resets the UI.
    const [draggingJobUuid, setDraggingJobUuid] = useState(null);
    const [dragOverColumnId, setDragOverColumnId] = useState(null);

    // Date filter — the kanban list is restricted to jobs created within a
    // single calendar month. Defaults to the current month; user can step
    // backward/forward or pick any month via the date input. The window is
    // always exactly one month — this matches the backend constraint that
    // rejects ranges longer than 31 days.
    const [selectedMonth, setSelectedMonth] = useState(currentMonthKey);

    /**
     * The month query behind the three finished-work columns — and, on the phone, the
     * whole list. jobsBoardQuery adds includeOpenJobs; without it a booking taken today
     * for next month lands in no column at all. The phone's ALL-jobs list adds a page
     * of a hundred with a total; "mine" and the desktop stay as they were.
     */
    const monthQuery = useCallback((bustCache = false) => ({
        ...jobsBoardQuery({ selectedMonth, myJobsOnly: isMobile && mobileMineOnly, bustCache }),
        ...(isMobile && !mobileMineOnly
            ? { page: mobilePage, size: MOBILE_JOBS_PAGE_SIZE, q: mobileSearch ?? '' }
            : {}),
    }), [selectedMonth, isMobile, mobileMineOnly, mobilePage, mobileSearch]);

    // ── Loan car return pre-check state ──
    // Holds the handover status fetched BEFORE showing the vehicle-collected confirm.
    // When non-null and hasOut && !hasIn, the LoanCarReturnModal is shown first.
    const [loanCarReturnJob, setLoanCarReturnJob]           = useState(null); // { uuid, jobNumber }
    const [loanCarHandoverStatus, setLoanCarHandoverStatus] = useState(null); // HandoverStatusResponse
    const [loanCarCheckLoading, setLoanCarCheckLoading]     = useState(false);

    useEffect(() => {
        if (showViewModal && selectedJob?.uuid) {
            setResourceTypeFilter('');
            dispatch(fetchJob(selectedJob.uuid));
            dispatch(fetchJobResources(selectedJob.uuid));
            dispatch(fetchAvailableResources({ jobUuid: selectedJob.uuid }));
        }
    }, [showViewModal, selectedJob?.uuid, dispatch]);

    useEffect(() => {
        // When the user is on a phone the dashboard should be scoped to jobs
        // assigned to them — supervisors and admins on desktop/tablet still
        // get the full org view. The backend's myJobsOnly flag does the
        // filtering server-side; we keep using fetchJobs (not fetchMyJobs)
        // because both paths share the same state.jobs.jobs slot, which
        // means downstream selectors and prop wiring stay identical.
        // jobsBoardQuery adds includeOpenJobs — without it a booking taken today
        // for next month lands in no column at all, and a job still in progress
        // drops off the board at midnight on the 1st. Shared with the refresh
        // button below and with the split view, which must not drift apart.
        dispatch(fetchJobs(monthQuery()));
        dispatch(fetchJobSummary());
        dispatch(fetchWorkCategories());
        dispatch(fetchCategoryTypes());
    }, [dispatch, monthQuery]);

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // Both sources: the month payload behind three columns AND the queued page behind
    // the fourth. Refreshing one and not the other is a board that disagrees with itself.
    const reloadQueued = queuedJobs.reload;
    const handleRefresh = useCallback(() => {
        dispatch(fetchJobs(monthQuery(true)));
        if (!isMobile) reloadQueued();
        dispatch(fetchJobSummary());
    }, [dispatch, monthQuery, isMobile, reloadQueued]);

    const handleToggleTv = useCallback((job) => {
        dispatch(setJobOnTv({ uuid: job.uuid, onTv: !job.onTv }));
    }, [dispatch]);

    // ── TV Mode auto-refresh ──
    // Only runs while the board is in TV Mode; the normal desktop view keeps its
    // manual refresh button. Each tick schedules the next one with a freshly
    // rolled delay (see nextTvPollDelay) so screens never converge on a shared
    // cadence. A hidden tab skips the fetch but keeps the chain alive, so a
    // backgrounded display costs the backend nothing and resumes on its own.
    const handleRefreshRef = useRef(handleRefresh);
    handleRefreshRef.current = handleRefresh;

    useEffect(() => {
        if (!isFullscreen) return undefined;

        let timerId;
        const tick = () => {
            if (!document.hidden) handleRefreshRef.current();
            timerId = setTimeout(tick, nextTvPollDelay());
        };

        timerId = setTimeout(tick, nextTvPollDelay());
        return () => clearTimeout(timerId);
    }, [isFullscreen]);

    // ── TV Mode keeps the session alive ──
    // A wall display has nobody in front of it, so the 15-minute inactivity
    // logout would show "Still there?" to an empty room and then drop the board
    // to the login screen. While TV Mode is on and the tab is actually visible,
    // it counts as activity. See useKeepSessionAlive for why this refreshes the
    // shared timestamp rather than just hiding the modal — the token refresh
    // depends on it too.
    useKeepSessionAlive(isFullscreen);

    useEffect(() => {
        if (showViewModal && selectedJob?.uuid) {
            dispatch(fetchAvailableResources({ jobUuid: selectedJob.uuid, type: resourceTypeFilter || null }));
        }
    }, [resourceTypeFilter]);

    useEffect(() => {
        if (showAssignModal) {
            setStaffSearch('');
            dispatch(fetchStaffList({ activeOnly: true, size: 100 }));

            // Also fetch leave plans for the job's scheduled window so we
            // can filter out staff who are on leave. Falls back to today
            // → today if the job has no scheduled dates yet.
            const todayIso = new Date().toISOString().slice(0, 10);
            const from = selectedJob?.scheduledStartDate || selectedJob?.bookingDate || todayIso;
            const to   = selectedJob?.scheduledEndDate   || from;
            dispatch(fetchAllLeavePlansInRange({ from, to }));
        }
    }, [showAssignModal, selectedJob?.scheduledStartDate, selectedJob?.scheduledEndDate, dispatch]);

    const handleAssignStaff = (staffMember) => {
        if (selectedJob) {
            dispatch(assignStaff({ uuid: selectedJob.uuid, staffUuid: staffMember.uuid }));
        }
    };

    const handleAssignResource = (resource) => {
        const resourceUuid = resource.uuid || resource.resourceUuid;
        if (!selectedJob || !resourceUuid) return;
        dispatch(assignResource({ jobUuid: selectedJob.uuid, data: { resourceUuid } }));
    };

    const handleUnassignResource = (resource) => {
        const resourceUuid = resource.resourceUuid || resource.uuid;
        if (!selectedJob || !resourceUuid) return;
        dispatch(unassignResource({ jobUuid: selectedJob.uuid, resourceUuid }));
    };

    // In TV Mode the three month-backed columns show only the jobs picked for the
    // wall. The Queued column does not filter here: its fetch already asked the server
    // for `onTv=true`, and a second filter on the page would just hide what it paged.
    const forTv = (list) => (isFullscreen ? list.filter(j => j.onTv) : list);

    // 🔑 The Queued column is drawn from its OWN page, never from the month payload.
    // The month payload still carries CREATED/QUOTED jobs (the split view and the
    // phone read them), but the board takes queued jobs from /jobs/queued alone —
    // so a job is never on the board twice and the badge is the count of all of them.
    const columns = [
        {
            id: 'queued',
            title: 'Queued',
            color: 'gray',
            statuses: QUEUED_COLUMN_STATUSES,
            // The page, narrowed locally. The badge stays the server's total.
            jobs: filterByNameOrRego(queuedJobs.jobs, queuedBox.text),
            total: queuedJobs.total,
            loading: queuedWaiting,
        },
        {
            id: 'in-progress',
            title: 'In Progress',
            color: 'blue',
            statuses: ['IN_PROGRESS'],
            jobs: forTv(jobs.filter(j => j.status === 'IN_PROGRESS')),
            loading: monthWaiting,
        },
        {
            id: 'paused',
            title: 'Waiting/Paused',
            color: 'amber',
            statuses: ['PAUSED'],
            jobs: forTv(jobs.filter(j => j.status === 'PAUSED')),
            loading: monthWaiting,
        },
        {
            id: 'completed',
            title: 'Completed',
            color: 'emerald',
            statuses: ['COMPLETED'],
            jobs: forTv(jobs.filter(j => j.status === 'COMPLETED')),
            loading: monthWaiting,
        },
    ];

    // When a status pill other than "All jobs" is active, narrow the board to
    // the matching column(s). 'all' shows every column.
    const visibleColumns = columnFilter === 'all'
        ? columns
        : columns.filter(col => col.id === columnFilter);

    const handleStartJob  = (job) => dispatch(startJob(job.uuid));
    const handlePauseJob  = (job) => dispatch(pauseJob({ uuid: job.uuid, reason: null }));
    const handleResumeJob = (job) => dispatch(resumeJob(job.uuid));

    // ── Kanban drag-and-drop handlers ──
    // The drop path goes through updateJobStatus rather than the dedicated
    // lifecycle thunks (startJob/pauseJob/resumeJob/completeJob). Drag is the
    // "I just want to change state" UI; the workflow buttons remain the
    // preferred path for the happy flow because they also stamp timestamps
    // and run side effects (vehicle handover emails, odometer prompts, etc.).
    // Backend validates the transition — anything illegal surfaces via the
    // standard operationError banner on the next render.
    const handleJobDragStart = (job) => setDraggingJobUuid(job.uuid);
    const handleJobDragEnd   = () => { setDraggingJobUuid(null); setDragOverColumnId(null); };

    const handleColumnDragOver = (e, columnId) => {
        // preventDefault is required for the column to be a valid drop target.
        // Without it the browser ignores the drop entirely.
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverColumnId !== columnId) setDragOverColumnId(columnId);
    };

    const handleColumnDragLeave = (e, columnId) => {
        // Only clear the highlight when leaving the column itself, not when
        // crossing between child elements (which fires dragleave repeatedly).
        if (e.currentTarget.contains(e.relatedTarget)) return;
        if (dragOverColumnId === columnId) setDragOverColumnId(null);
    };

    const handleColumnDrop = (e, column) => {
        e.preventDefault();
        setDragOverColumnId(null);
        const droppedUuid = e.dataTransfer.getData('text/job-uuid') || draggingJobUuid;
        setDraggingJobUuid(null);
        if (!droppedUuid) return;
        // Use `jobs` (the slice's job list) directly rather than `allJobs`
        // (which is a flatMap derived further down in the component body).
        // Avoids any temporal-dead-zone surprises if the function ever gets
        // moved around during refactors.
        const droppedJob = jobs.find(j => j.uuid === droppedUuid);
        if (!droppedJob) return;
        // Already in the target column — no-op so we don't dispatch a
        // redundant request (and don't have to pick "which status" the
        // grouped Queued column should pick when dropping a job that's
        // already, say, APPROVED into Queued).
        if (column.statuses.includes(droppedJob.status)) return;
        // Each column maps to one or more statuses; the first one is the
        // canonical destination. For Queued (CREATED/QUOTED/APPROVED) we
        // always send the job back to CREATED — the safest reset.
        const targetStatus = column.statuses[0];
        dispatch(updateJobStatus({ uuid: droppedJob.uuid, status: targetStatus }));
    };

    const handleCompleteJob = () => {
        if (!selectedJob || !odometerOut) return;
        dispatch(completeJob({ uuid: selectedJob.uuid, odometerOut: parseInt(odometerOut) }));
        setOdometerOut('');
    };

    /**
     * Called when staff taps "Vehicle Collected" on a job.
     *
     * Pre-checks the loan car handover status:
     *   - If the booking has a loan car with OUT recorded but no IN yet →
     *     show LoanCarReturnModal first so staff can record the return.
     *   - Otherwise → go straight to the VehicleCollectedConfirm dialog.
     *
     * The actual PATCH /jobs/{uuid}/vehicle-collected call only happens
     * inside handleVehicleCollected, after the staff confirms in the dialog.
     */
    const handleOpenVehicleCollect = useCallback(async (job) => {
        dispatch(selectJob(job));
        dispatch(closeViewModal());

        setLoanCarCheckLoading(true);
        try {
            const status = await getLoanCarHandoverStatusApi(job.uuid);
            if (status.hasOut && !status.hasIn) {
                // Loan car was handed out — need to record return first
                setLoanCarHandoverStatus(status);
                setLoanCarReturnJob(job);
            } else {
                // No loan car, or full round-trip already done — go straight to confirm
                setLoanCarHandoverStatus(null);
                setLoanCarReturnJob(null);
                dispatch(openVehicleCollectedConfirm(job));
            }
        } catch {
            // If the check fails (network, permissions) — fall through to confirm anyway
            setLoanCarHandoverStatus(null);
            setLoanCarReturnJob(null);
            dispatch(openVehicleCollectedConfirm(job));
        } finally {
            setLoanCarCheckLoading(false);
        }
    }, [dispatch]);

    /**
     * Called by LoanCarReturnModal when the staff either records the IN
     * handover or explicitly skips it. Either way we proceed to the
     * vehicle-collected confirmation dialog.
     */
    const handleLoanCarReturnDone = useCallback(({ recorded }) => {
        const job = loanCarReturnJob;
        setLoanCarReturnJob(null);
        setLoanCarHandoverStatus(null);

        if (recorded) {
            dispatch(setOperationSuccess('Loan car return recorded'));
        }

        // Now open the normal vehicle-collected confirmation
        dispatch(openVehicleCollectedConfirm(job));
    }, [dispatch, loanCarReturnJob]);

    /**
     * Fires the actual PATCH /jobs/{uuid}/vehicle-collected after the staff
     * confirms in the VehicleCollectedConfirm dialog.
     */
    /**
     * Seed the next-service boxes when the collect dialog opens.
     *
     * ⚠️ Keyed on the dialog opening, not on the job, so reopening it after a change of
     * mind starts from the suggestion again rather than from a stale edit.
     */
    useEffect(() => {
        if (!showVehicleCollectedConfirm || !selectedJob) return;
        const odometer = selectedJob.odometerOut ?? selectedJob.vehicle?.odometerKm ?? null;
        const suggested = suggestNextService(odometer);
        setNextServiceDue(suggested.nextServiceDue);
        setNextServiceKm(suggested.nextServiceKm == null ? '' : String(suggested.nextServiceKm));
    }, [showVehicleCollectedConfirm, selectedJob]);

    const handleVehicleCollected = () => {
        if (!selectedJob) return;
        dispatch(vehicleCollected({
            uuid: selectedJob.uuid,
            nextServiceDue: nextServiceDue || null,
            // An empty box means "do not record one", not zero.
            nextServiceKm: nextServiceKm === '' ? null : Number(nextServiceKm),
        }));
    };

    const handleCancelJob = () => {
        if (selectedJob) {
            dispatch(cancelJob({ uuid: selectedJob.uuid, reason: cancelReason || null }));
            setCancelReason('');
        }
    };

    // Work items and notes on the open job. Shared with the phone's quote detail
    // screen, which does the same five things to the same job — see the hook.
    const {
        addWorkItem: handleAddWorkItem,
        editWorkItem: handleEditWorkItem,
        removeWorkItem: handleRemoveWorkItem,
        addNote: handleAddNoteFromTab,
        refreshJob: handleRefreshJob,
    } = useJobDetailActions(selectedJob);

    /**
     * Link a measurement to an ad-hoc work item.
     * Returns the dispatch promise so the picker can await + close on success.
     */
    const handleLinkMeasurement = async (workItemUuid, measurementUuid) => {
        if (!selectedJob || !workItemUuid || !measurementUuid) return null;
        const resultAction = await dispatch(linkMeasurement({
            jobUuid: selectedJob.uuid,
            workItemUuid,
            measurementUuid,
        }));
        if (linkMeasurement.fulfilled.match(resultAction)) return resultAction.payload;
        throw new Error(resultAction.payload || 'Failed to link measurement');
    };

    /**
     * Unlink a measurement from an ad-hoc work item.
     */
    const handleUnlinkMeasurement = async (workItemUuid, measurementUuid) => {
        if (!selectedJob || !workItemUuid || !measurementUuid) return null;
        const resultAction = await dispatch(unlinkMeasurement({
            jobUuid: selectedJob.uuid,
            workItemUuid,
            measurementUuid,
        }));
        if (unlinkMeasurement.fulfilled.match(resultAction)) return resultAction.payload;
        throw new Error(resultAction.payload || 'Failed to unlink measurement');
    };

    const handleViewJob = (job) => {
        setInitialModalTab('details');
        dispatch(selectJob(job));
        dispatch(openViewModal(job));
    };

    /**
     * 🔑 `?job=<uuid>` opens that job over the board.
     *
     * What an invoice's job link lands on: the board is where a job belongs, so
     * following the link leaves the invoice behind entirely rather than stacking a job
     * card over an invoice table and its side panel. The card opens on the uuid alone
     * and the existing detail effect fetches the rest, so it shows its own shapes while
     * that is in flight rather than a blank card.
     */
    const openJobFromLink = useCallback((uuid) => {
        setInitialModalTab('details');
        dispatch(selectJob({ uuid }));
        dispatch(openViewModal({ uuid }));
    }, [dispatch]);
    useConsumeRecordParam(JOB_PARAM, openJobFromLink);

    const allJobs        = columns.flatMap(col => col.jobs);

    if (showPlanner) return (
        <JobsPlannerPage
            onBack={() => setShowPlanner(false)}
            onSwitchToTablet={() => { setShowPlanner(false); setForceTabletView(true); }}
        />
    );
    if (showSearch)  return <JobsSearchPage  onBack={() => setShowSearch(false)} />;

    // ── Mobile / Mechanic's phone view ────────────────────────────────────────
    //
    // MobileShell, not Layout: Layout's sidebar and hamburger drawer list every
    // page in the product, and the phone build carries only Jobs, Bookings and
    // Invoices (see the mobile route block in App.jsx). The shell provides the bottom tab
    // bar, the offline/update banners and the safe-area insets instead.
    if (isMobile) {
        return (
            <MobileShell>
                <MobileJobsDashboard
                    scope={mobileMineOnly ? 'mine' : 'all'}
                    onScopeChange={(next) => setMobileMineOnly(next === 'mine')}
                    jobs={jobs}
                    jobsLoading={jobsLoading}
                    // All jobs come twenty at a time with a total; "mine" is one list.
                    onSearchTerm={mobileMineOnly ? null : onMobileSearch}
                    paging={mobileMineOnly ? null : {
                        page: mobilePage,
                        totalPages: pagination.totalPages,
                        total: pagination.totalElements,
                        size: MOBILE_JOBS_PAGE_SIZE,
                        onPage: setMobilePage,
                    }}
                    operationLoading={operationLoading}
                    workCategories={workCategories}
                    categoryTypes={categoryTypes}
                    onRefresh={handleRefresh}
                    onStartJob={handleStartJob}
                    onPauseJob={handlePauseJob}
                    onResumeJob={handleResumeJob}
                    onOpenComplete={(j) => { dispatch(selectJob(j)); dispatch(openCompleteConfirm(j)); }}
                    onAddWorkItem={handleAddWorkItem}
                    onEditWorkItem={handleEditWorkItem}
                    onRemoveWorkItem={handleRemoveWorkItem}
                    onAddNote={handleAddNoteFromTab}
                    onRefreshJob={handleRefreshJob}
                />

                {/* Toast notifications */}
                {operationSuccess && (
                    <Toast message={operationSuccess} type="success" onDismiss={() => dispatch(clearOperationStatus())} />
                )}
                {operationError && !showCompleteConfirm && (
                    <Toast message={operationError} type="error" onDismiss={() => dispatch(clearOperationStatus())} />
                )}

                {/* Complete confirm modal — bottom sheet on mobile */}
                {showCompleteConfirm && selectedJob && (
                    <div className="fixed inset-0 bg-black/50 flex items-end z-[60]">
                        <div className="bg-white rounded-t-2xl w-full">
                            <div className="p-5 border-b flex items-center gap-3">
                                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0">
                                    <CheckCircle size={18} className="text-emerald-600" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900">Complete Job</h2>
                                    <p className="text-sm text-gray-500">{selectedJob.jobNumber}</p>
                                </div>
                            </div>
                            <div className="p-5 space-y-4">
                                {operationError && (
                                    <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                                        <AlertTriangle size={15} className="text-red-500 shrink-0 mt-0.5" />
                                        <p className="text-sm text-red-700">{operationError}</p>
                                    </div>
                                )}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Odometer Out <span className="text-red-500">*</span></label>
                                    <input type="number" value={odometerOut}
                                           onChange={(e) => { const v = e.target.value; const n = parseInt(v); setOdometerOut(isNaN(n) || n < 0 ? '' : String(n)); }}
                                           placeholder="e.g. 85000" min="0"
                                           className={`w-full border rounded-lg p-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500
                                               [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                                               ${!odometerOut ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`} />
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => { dispatch(closeCompleteConfirm()); setOdometerOut(''); }}
                                            className="flex-1 py-3 border rounded-xl active:bg-gray-50 text-sm min-h-[48px]">Cancel</button>
                                    <button onClick={handleCompleteJob} disabled={operationLoading || !odometerOut}
                                            className="flex-1 py-3 bg-emerald-600 text-white rounded-xl active:bg-emerald-700 disabled:opacity-50 text-sm font-medium min-h-[48px]">
                                        {operationLoading ? 'Completing...' : 'Complete Job'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </MobileShell>
        );
    }

    // ── Tablet / iPad split-panel view ────────────────────────────────────────
    if (showTabletView) {
        return (
            <JobsTabletView
                onSwitchToKanban={() => setForceTabletView(false)}
                onSwitchToPlanner={() => { setForceTabletView(false); setShowPlanner(true); }}
            />
        );
    }

    return (
        <Layout>
            <div className={`flex flex-col h-full overflow-hidden bg-gray-100 ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>

                {/* Toast Notifications */}
                {operationSuccess && (
                    <Toast message={operationSuccess} type="success" onDismiss={() => dispatch(clearOperationStatus())} />
                )}
                {operationError && !showCompleteConfirm && (
                    <Toast message={operationError} type="error" onDismiss={() => dispatch(clearOperationStatus())} />
                )}

                {/* Loan car pre-check loading overlay */}
                {loanCarCheckLoading && (
                    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[55]">
                        <div className="bg-white rounded-2xl px-6 py-5 flex items-center gap-3 shadow-xl">
                            <RefreshCw size={18} className="animate-spin text-sky-600" />
                            <span className="text-sm font-medium text-gray-700">Checking loan car status…</span>
                        </div>
                    </div>
                )}

                {/* ── Shared toolbar — identical structure across Kanban / Tablet / Planner. ──
                    Inline summary pills go in the title-extras slot; the search-history page
                    and TV-mode buttons live in right-extras (kanban-only). */}
                <JobsToolbar
                    activeView="kanban"
                    // TV Mode is a wall, not a desk: no view switcher, no title block.
                    chromeless={isFullscreen}
                    onSwitchView={(id) => {
                        if (id === 'kanban')  return; // already here
                        if (id === 'tablet')  setForceTabletView(true);
                        if (id === 'planner') setShowPlanner(true);
                    }}
                    title="Jobs Board"
                    subtitle={`${currentTime.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })} · ${allJobs.length} active`}
                    titleExtras={
                        <>
                            {COLUMN_FILTERS.map((f) => {
                                const isActive = columnFilter === f.id;
                                // Queued is counted by the server — ALL of them, not the
                                // page on screen. The rest are on the board in full.
                                const count = f.id === 'queued'
                                    ? queuedJobs.total
                                    : f.statuses
                                        ? jobs.filter(j => f.statuses.includes(j.status)).length
                                        : null;
                                return (
                                    <button
                                        key={f.id}
                                        type="button"
                                        onClick={() => setColumnFilter(f.id)}
                                        className={`shrink-0 h-7 text-[11px] font-semibold px-2.5 rounded-full transition-colors border
                                            ${isActive
                                            ? 'bg-violet-600 text-white border-violet-600'
                                            : 'bg-white text-gray-500 border-gray-200 hover:border-violet-300 hover:text-violet-600'}`}
                                    >
                                        {f.label}
                                        {count != null && (
                                            <span className="ml-1 opacity-75">{count}</span>
                                        )}
                                    </button>
                                );
                            })}
                        </>
                    }
                    selectedMonth={selectedMonth}
                    setSelectedMonth={setSelectedMonth}
                    onRefresh={handleRefresh}
                    refreshing={jobsLoading}
                    rightExtras={
                        <>
                            <button
                                type="button"
                                onClick={() => setShowSearch(true)}
                                title="Job History & Search"
                                className="h-9 w-9 flex items-center justify-center bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors shrink-0"
                            >
                                <TableProperties size={15} />
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsFullscreen(!isFullscreen)}
                                title={isFullscreen ? 'Exit TV Mode' : 'TV Mode'}
                                className="flex items-center gap-1.5 h-9 px-3 bg-violet-600 text-white rounded-xl hover:bg-violet-700 transition-colors text-xs font-semibold shrink-0"
                            >
                                <Maximize2 size={14} />
                                <span className="hidden lg:inline">
                                    {isFullscreen ? 'Exit TV' : 'TV Mode'}
                                </span>
                            </button>
                        </>
                    }
                />

                {/* ── Viewport — scrollable kanban grid below the locked toolbar ── */}
                <div className="flex-1 overflow-auto p-4">
                    <div
                        className="grid gap-3 h-full"
                        style={{ gridTemplateColumns: `repeat(${visibleColumns.length}, minmax(0, 1fr))` }}
                    >
                        {visibleColumns.map((column) => {
                            const isDropTarget = dragOverColumnId === column.id;
                            return (
                                <div
                                    key={column.id}
                                    onDragOver={(e) => handleColumnDragOver(e, column.id)}
                                    onDragLeave={(e) => handleColumnDragLeave(e, column.id)}
                                    onDrop={(e) => handleColumnDrop(e, column)}
                                    className={`rounded-2xl border-2 ${getColumnColor(column.color)} flex flex-col min-h-0 transition-all ${isDropTarget ? 'ring-2 ring-violet-400 ring-offset-2 ring-offset-white scale-[1.01]' : ''}`}
                                >
                                    <div className="p-3 border-b border-gray-200 shrink-0">
                                        <div className="flex items-center justify-between">
                                            <h3 className="font-semibold text-gray-900 text-sm">{column.title}</h3>
                                            <span
                                                data-testid={`column-count-${column.id}`}
                                                className={`px-2 py-0.5 rounded-full text-xs font-medium ${column.color === 'emerald' ? 'bg-emerald-200 text-emerald-800' :
                                                column.color === 'blue' ? 'bg-blue-200 text-blue-800' :
                                                    column.color === 'amber' ? 'bg-amber-200 text-amber-800' :
                                                        'bg-gray-200 text-gray-800'
                                            }`}>
                                            {column.total ?? column.jobs.length}
                                        </span>
                                        </div>
                                        {column.id === 'queued' && (
                                            <PagedSearchControls
                                                className="mt-2"
                                                search={queuedBox.text}
                                                onSearch={queuedBox.setText}
                                                error={queuedBox.error}
                                                page={queuedJobs.page}
                                                totalPages={queuedJobs.totalPages}
                                                total={queuedJobs.total}
                                                size={queuedJobs.size}
                                                onPage={queuedJobs.setPage}
                                                searchLabel="Search queued jobs"
                                                pagerLabel="queued jobs"
                                                alwaysShowPager
                                            />
                                        )}
                                    </div>
                                    <div className="p-2 overflow-y-auto flex-1 min-h-0">
                                        {/* The wait, from this column's own source. "No jobs"
                                            is a claim and is only made once the load is done. */}
                                        {column.loading && <LaneLoading label={`Loading ${column.title.toLowerCase()} jobs`} />}
                                        {!column.loading && column.jobs.map((job) => (
                                            <JobCard
                                                key={job.uuid}
                                                job={job}
                                                currentTime={currentTime}
                                                onView={handleViewJob}
                                                onStart={handleStartJob}
                                                onPause={handlePauseJob}
                                                onResume={handleResumeJob}
                                                onComplete={(j) => { dispatch(selectJob(j)); dispatch(openCompleteConfirm(j)); }}
                                                onCancel={(j) => { dispatch(selectJob(j)); dispatch(openCancelConfirm(j)); }}
                                                onNote={(j) => { setInitialModalTab('notes'); dispatch(selectJob(j)); dispatch(openViewModal(j)); }}
                                                onAssign={(j) => { dispatch(selectJob(j)); dispatch(openAssignModal(j)); }}
                                                onToggleTv={handleToggleTv}
                                                onDragStart={handleJobDragStart}
                                                onDragEnd={handleJobDragEnd}
                                                isDragging={draggingJobUuid === job.uuid}
                                            />
                                        ))}
                                        {!column.loading && column.jobs.length === 0 && (
                                            <div className={`text-center py-8 text-sm transition-colors ${isDropTarget ? 'text-violet-500 font-medium' : 'text-gray-400'}`}>
                                                {isDropTarget ? 'Release to move job here' : 'No jobs'}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Job Detail Modal */}
                {showViewModal && selectedJob && (
                    <JobDetailModal
                        onOpenInvoice={openInvoice}
                        job={selectedJob}
                        initialTab={initialModalTab}
                        operationLoading={operationLoading}
                        detailLoading={detailLoading}
                        jobResources={jobResources}
                        availableResources={availableResources}
                        resourcesLoading={resourcesLoading}
                        workCategories={workCategories}
                        categoryTypes={categoryTypes}
                        onClose={() => dispatch(closeViewModal())}
                        onStart={handleStartJob}
                        onPause={handlePauseJob}
                        onResume={handleResumeJob}
                        onOpenComplete={(j) => dispatch(openCompleteConfirm(j))}
                        onOpenVehicleCollect={handleOpenVehicleCollect}
                        onOpenAssign={(j) => dispatch(openAssignModal(j))}
                        onAddWorkItem={handleAddWorkItem}
                        onEditWorkItem={handleEditWorkItem}
                        onRemoveWorkItem={handleRemoveWorkItem}
                        onLinkMeasurement={handleLinkMeasurement}
                        onUnlinkMeasurement={handleUnlinkMeasurement}
                        onAddNote={handleAddNoteFromTab}
                        onRefreshJob={handleRefreshJob}
                        onAssignResource={handleAssignResource}
                        onUnassignResource={handleUnassignResource}
                        resourceTypeFilter={resourceTypeFilter}
                        onResourceTypeFilter={setResourceTypeFilter}
                    />
                )}

                {/* Assign Staff Modal */}
                {showAssignModal && selectedJob && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl w-full max-w-md flex flex-col max-h-[80vh] shadow-2xl">
                            <div className="px-5 py-4 border-b flex items-center justify-between shrink-0">
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900">Assign Staff</h2>
                                    <p className="text-sm text-gray-500 mt-0.5">
                                        <span className="font-medium text-gray-700">{selectedJob.jobNumber}</span>
                                        {selectedJob.assignedStaffName && (
                                            <span className="ml-1">· currently <span className="text-violet-600">{selectedJob.assignedStaffName}</span></span>
                                        )}
                                    </p>
                                </div>
                                <button onClick={() => dispatch(closeAssignModal())} className="p-2 hover:bg-gray-100 rounded-xl">
                                    <X size={20} />
                                </button>
                            </div>
                            <div className="px-4 py-3 border-b shrink-0">
                                <div className="relative">
                                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search by name or role..."
                                        value={staffSearch}
                                        onChange={(e) => setStaffSearch(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                                        autoFocus
                                    />
                                </div>
                            </div>
                            <div className="overflow-y-auto flex-1 p-3">
                                {staffLoading ? (
                                    <div className="flex items-center justify-center gap-2 py-10 text-gray-400 text-sm">
                                        <RefreshCw size={15} className="animate-spin" /> Loading staff…
                                    </div>
                                ) : (() => {
                                    const searchTerm = staffSearch.toLowerCase();
                                    const searched = (staffList || []).filter(s =>
                                        !staffSearch ||
                                        s.fullName?.toLowerCase().includes(searchTerm) ||
                                        s.role?.toLowerCase().includes(searchTerm)
                                    );

                                    // Partition: on-leave staff are kept in a
                                    // separate bucket so the user still sees
                                    // them but can't pick them by accident.
                                    const available = [];
                                    const onLeave   = [];
                                    for (const s of searched) {
                                        const leaves = leavePlansByStaff[s.uuid];
                                        if (leaves && leaves.length > 0) {
                                            onLeave.push({ staff: s, leaves });
                                        } else {
                                            available.push(s);
                                        }
                                    }

                                    if (available.length === 0 && onLeave.length === 0) return (
                                        <div className="text-center py-10 text-gray-400 text-sm">No staff found</div>
                                    );

                                    const fmtLeave = (leaves) => {
                                        const lp = leaves[0];
                                        const start = new Date(`${lp.startDate}T00:00:00`);
                                        const end   = new Date(`${lp.endDate}T00:00:00`);
                                        const fmt   = (d) => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
                                        return `On leave ${fmt(start)} → ${fmt(end)}${leaves.length > 1 ? ` (+${leaves.length - 1} more)` : ''}`;
                                    };

                                    return (
                                        <div className="space-y-1">
                                            {available.map((staff) => {
                                                const isAssigned = selectedJob.assignedStaffUuid === staff.uuid;
                                                const initials = `${staff.firstName?.[0] || ''}${staff.lastName?.[0] || ''}`.toUpperCase() || '?';
                                                return (
                                                    <button
                                                        key={staff.uuid}
                                                        onClick={() => handleAssignStaff(staff)}
                                                        disabled={operationLoading || isAssigned}
                                                        className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors border-2
                                                            ${isAssigned
                                                            ? 'bg-violet-50 border-violet-300 cursor-default'
                                                            : 'border-transparent hover:bg-gray-50 hover:border-gray-200'
                                                        }`}
                                                    >
                                                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0
                                                            ${isAssigned ? 'bg-violet-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                                                            {initials}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-semibold text-gray-900 truncate">{staff.fullName}</p>
                                                            <p className="text-xs text-gray-500 truncate">{staff.role?.replace(/_/g, ' ')}</p>
                                                        </div>
                                                        {isAssigned && (
                                                            <span className="text-xs text-violet-600 font-medium shrink-0">Assigned</span>
                                                        )}
                                                        {operationLoading && !isAssigned && (
                                                            <RefreshCw size={13} className="animate-spin text-gray-300 shrink-0" />
                                                        )}
                                                    </button>
                                                );
                                            })}

                                            {onLeave.length > 0 && (
                                                <div className="pt-3 mt-3 border-t border-gray-200">
                                                    <p className="px-1 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                                        On leave · not available
                                                    </p>
                                                    {onLeave.map(({ staff, leaves }) => {
                                                        const initials = `${staff.firstName?.[0] || ''}${staff.lastName?.[0] || ''}`.toUpperCase() || '?';
                                                        return (
                                                            <div
                                                                key={staff.uuid}
                                                                className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-transparent bg-gray-50/60 opacity-70 cursor-not-allowed"
                                                                title={fmtLeave(leaves)}
                                                            >
                                                                <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 bg-gray-200 text-gray-500">
                                                                    {initials}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-sm font-semibold text-gray-700 truncate">{staff.fullName}</p>
                                                                    <p className="text-xs text-amber-600 truncate">{fmtLeave(leaves)}</p>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                            {selectedJob.assignedStaffUuid && (
                                <div className="px-4 py-3 border-t shrink-0">
                                    <button
                                        onClick={() => dispatch(unassignStaff(selectedJob.uuid))}
                                        disabled={operationLoading}
                                        className="w-full py-2 text-sm text-red-500 hover:bg-red-50 rounded-xl border border-red-200 hover:border-red-300 transition-colors"
                                    >
                                        Remove Assignment
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Complete Confirm Modal */}
                {showCompleteConfirm && selectedJob && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-2xl w-full max-w-md m-4">
                            <div className="p-6 border-b flex items-center gap-3">
                                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0">
                                    <CheckCircle size={18} className="text-emerald-600" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900">Complete Job</h2>
                                    <p className="text-sm text-gray-500 mt-0.5">Mark {selectedJob.jobNumber} as completed?</p>
                                </div>
                            </div>
                            <div className="p-6 space-y-4">
                                {operationError && (
                                    <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                                        <AlertTriangle size={15} className="text-red-500 shrink-0 mt-0.5" />
                                        <p className="text-sm text-red-700 leading-snug">{operationError}</p>
                                    </div>
                                )}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Odometer Out <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            value={odometerOut}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                const num = parseInt(val);
                                                setOdometerOut(isNaN(num) || num < 0 ? '' : String(num));
                                            }}
                                            placeholder="e.g. 85000"
                                            min="0"
                                            max="9999999"
                                            className={`w-full border rounded-lg p-3 pr-10 focus:outline-none focus:ring-2 focus:ring-emerald-500
                                                [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                                                ${!odometerOut ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`}
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium pointer-events-none">
                                            km
                                        </span>
                                    </div>
                                    {!odometerOut && (
                                        <p className="mt-1 text-xs text-red-500">Required — record the odometer reading when returning the vehicle</p>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => { dispatch(closeCompleteConfirm()); setOdometerOut(''); }}
                                        className="flex-1 py-2 border rounded-lg hover:bg-gray-50 text-sm"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleCompleteJob}
                                        disabled={operationLoading || !odometerOut}
                                        className="flex-1 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium"
                                    >
                                        {operationLoading ? 'Completing...' : 'Complete Job'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Loan Car Return Modal ──
                    Shown BEFORE the vehicle-collected confirm when the booking
                    has a loan car with OUT recorded but no IN yet.               */}
                {loanCarReturnJob && loanCarHandoverStatus && (
                    <LoanCarReturnModal
                        jobUuid={loanCarReturnJob.uuid}
                        handoverStatus={loanCarHandoverStatus}
                        onDone={handleLoanCarReturnDone}
                    />
                )}

                {/* Vehicle Collected Confirm Modal */}
                {showVehicleCollectedConfirm && selectedJob && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-2xl w-full max-w-md m-4">
                            <div className="p-6 border-b">
                                <div className="flex items-center gap-3 mb-1">
                                    <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center">
                                        <Car size={18} className="text-sky-600" />
                                    </div>
                                    <h2 className="text-lg font-bold text-gray-900">Vehicle Collected</h2>
                                </div>
                                <p className="text-sm text-gray-500 mt-1">
                                    Confirm that the customer has collected their vehicle for{' '}
                                    <span className="font-semibold text-gray-700">{selectedJob.jobNumber}</span>?
                                </p>
                                <p className="text-xs text-gray-400 mt-2">
                                    This will remove the job from the board and mark the booking as completed.
                                </p>
                            </div>

                            {/* Handing back the keys is when the customer is told when to
                                come back, so it is where that gets recorded. Both are
                                optional: clear them both to record nothing. */}
                            <div className="px-6 py-4 border-b bg-gray-50/60">
                                <p className="text-xs font-semibold text-gray-700 mb-2">
                                    Next service due
                                </p>
                                <div className="grid grid-cols-2 gap-3">
                                    <label className="block">
                                        <span className="text-[11px] text-gray-500">Date</span>
                                        <input
                                            type="date"
                                            value={nextServiceDue}
                                            onChange={(e) => setNextServiceDue(e.target.value)}
                                            className="mt-1 w-full px-2.5 py-2 border border-gray-200 rounded-lg text-sm"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="text-[11px] text-gray-500">Odometer (km)</span>
                                        <input
                                            type="number"
                                            min="0"
                                            value={nextServiceKm}
                                            onChange={(e) => setNextServiceKm(e.target.value)}
                                            placeholder="—"
                                            className="mt-1 w-full px-2.5 py-2 border border-gray-200 rounded-lg text-sm"
                                        />
                                    </label>
                                </div>
                                <p className="text-[11px] text-gray-500 mt-2">
                                    Whichever comes first. Clear both to record nothing.
                                </p>
                            </div>
                            <div className="p-6 flex gap-2">
                                <button
                                    onClick={() => dispatch(closeVehicleCollectedConfirm())}
                                    className="flex-1 py-2 border rounded-lg hover:bg-gray-50 text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleVehicleCollected}
                                    disabled={operationLoading}
                                    className="flex-1 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 text-sm font-medium"
                                >
                                    {operationLoading ? 'Processing...' : 'Confirm Collection'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Cancel Confirm Modal */}
                {showCancelConfirm && selectedJob && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-2xl w-full max-w-md m-4">
                            <div className="p-6 border-b">
                                <h2 className="text-lg font-bold text-gray-900">Cancel Job</h2>
                                <p className="text-sm text-gray-500 mt-1">Are you sure you want to cancel {selectedJob.jobNumber}?</p>
                            </div>
                            <div className="p-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Reason (optional)</label>
                                <textarea
                                    value={cancelReason}
                                    onChange={(e) => setCancelReason(e.target.value)}
                                    placeholder="Enter cancellation reason..."
                                    className="w-full border rounded-lg p-3 h-24 resize-none focus:outline-none focus:ring-2 focus:ring-red-500"
                                />
                                <div className="flex gap-2 mt-4">
                                    <button onClick={() => dispatch(closeCancelConfirm())} className="flex-1 py-2 border rounded-lg hover:bg-gray-50">
                                        Keep Job
                                    </button>
                                    <button
                                        onClick={handleCancelJob}
                                        disabled={operationLoading}
                                        className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                                    >
                                        {operationLoading ? 'Cancelling...' : 'Cancel Job'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default JobsPage;